/**
 * Maps `/shop/:category` slug → `/products` query params (server controller).
 *
 * When `categoryRoots` is provided (from GET /categories?tree=true), the same resolution as
 * shop/client: prefer matching a real Category `_id` or `slug` from the store tree so
 * `GET /products?category=women` does not 404-resolve to empty when Mongo slug is `womens`, etc.
 *
 * @returns Record<string, string | number | boolean> | undefined
 * `undefined` means “skip API for this PLP” (use static catalogue only).
 */

import { findCategoryNodeForShopSlug } from './shopCategoryApiResolve';

function slugJsonOverrides() {
  try {
    const raw = (process.env.REACT_APP_SHOP_CATEGORY_SLUG_JSON || '').trim();
    if (!raw) return {};
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

const SLUG_OVERRIDES = slugJsonOverrides();

/** Manual slug map from .env (highest priority after tree). */
function envOverrideCategoryValue(shopSlug) {
  const k = shopSlug.toLowerCase();
  const v = SLUG_OVERRIDES[k];
  if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
}

/**
 * @param {string} categorySlug - route segment e.g. "women"
 * @param {unknown[]} [categoryRoots] - Redux `category.roots` from fetchCategories
 */
export function buildShopProductApiParams(categorySlug, categoryRoots) {
  const cat = (categorySlug || 'all').toLowerCase();
  if (!cat || cat === 'all') return {};
  if (cat === 'sale') return { sale: 'true' };
  if (cat === 'new') return { new: 'true' };
  /** No server “bestsellers” flag — fetch broad list; PLP may filter client-side by `tags`. */
  if (cat === 'bestsellers') return {};

  const fromEnv = envOverrideCategoryValue(cat);
  if (fromEnv) return { category: fromEnv };

  const node = findCategoryNodeForShopSlug(categoryRoots || [], cat);
  if (node) {
    const id = node._id != null ? String(node._id).trim() : '';
    if (id && /^[a-f0-9]{24}$/i.test(id)) {
      return { category: id };
    }
    const slug = node.slug != null ? String(node.slug).trim() : '';
    if (slug) return { category: slug };
  }

  return { category: cat };
}
